import { useCallback } from 'react'

import {
  DescriptionList,
  Heading,
  SkeletonBox,
  TextLink,
} from '@circlefin/components'
import { useDate, useMoney } from '@circlefin/hooks'
import { HistoryBackButton } from '@containers/layout'
import { useSettlementCreditLine } from '@features/settlement-advance/hooks/settlement-credit-line'
import { lineOfCreditDetailSection } from '@services/sections/lib/lineOfCredit'
import { settlementAdvanceDetailSection } from '@services/sections/lib/settlementAdvance'
import { currencyToToken } from '@shared/components/common'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import { Content } from '@shared/components/layout'
import {
  SettlementCreditLineType,
  useSettlementCreditTransfersQuery,
} from '@shared/graphql'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import type { ApolloError } from '@apollo/client'
import type { SettlementCreditReceive } from '@shared/graphql'

export interface SettlementAdvanceReceiveDetailProps {
  /**
   * The transaction we want to render.
   */
  transaction?: SettlementCreditReceive
  /**
   * Error from fetching transaction details.
   */
  error?: ApolloError
}

export const SettlementAdvanceReceiveDetail = ({
  error,
  transaction,
}: SettlementAdvanceReceiveDetailProps) => {
  const { t } = useTranslation('payments')
  const router = useRouter()
  const { money } = useMoney()
  const { dateNumeric: formatDate } = useDate()
  const { loading: settlementCreditLineLoading, settlementCreditLineType } =
    useSettlementCreditLine()

  const {
    data,
    loading,
    error: transferError,
  } = useSettlementCreditTransfersQuery({
    variables: {
      page: { pageSize: 1 },
      filter: {
        receiveJobId: transaction?.payment.id || '',
      },
    },
    skip: transaction?.payment.id === undefined,
  })

  const handleViewSettlementCreditTransferClick = useCallback(() => {
    const section =
      settlementCreditLineType === SettlementCreditLineType.settlementAdvance
        ? settlementAdvanceDetailSection
        : lineOfCreditDetailSection

    void router.push({
      pathname: section.route,
      query: {
        id: data?.settlementCreditTransfers.list[0]?.id,
      },
    })
  }, [data?.settlementCreditTransfers.list, router, settlementCreditLineType])

  if (settlementCreditLineLoading) return null

  return (
    <Content>
      <HistoryBackButton />
      <Heading variant="page/sm">
        <Heading.Eyebrow>
          {t(`settlementCreditReceiveDetail.${settlementCreditLineType}.title`)}
        </Heading.Eyebrow>
        <Heading.Header>
          {transaction?.payment.amount &&
            money({
              number: transaction.payment.amount.amount,
              variant: currencyToToken(transaction.payment.amount.currency),
              options: {
                symbol: true,
                signDisplay: false,
              },
            })}
        </Heading.Header>
        {transaction?.payment.id && (
          <Heading.Intro>
            {t('transactionDetail.transactionId', {
              id: transaction.payment.id,
            })}
          </Heading.Intro>
        )}
      </Heading>

      <GraphQLErrorBoundary error={error || transferError}>
        <div className="flex flex-col gap-2">
          <DescriptionList density="loose">
            <DescriptionList.Label className="w-64">
              {t('settlementCreditReceiveDetail.type')}
            </DescriptionList.Label>
            <DescriptionList.Description>
              <p className="type-body-base-bold">
                {t(
                  `settlementCreditReceiveDetail.${settlementCreditLineType}.title`,
                )}
              </p>
            </DescriptionList.Description>
            <DescriptionList.Label className="w-64">
              {t('settlementCreditReceiveDetail.status')}
            </DescriptionList.Label>
            <DescriptionList.Description className="flex flex-col justify-center gap-1">
              <p className="type-body-base-bold">
                {t(
                  `settlementCreditReceiveDetail.statusLabels.${transaction?.payment.status}`,
                )}
              </p>
              <p className="type-body-xs">
                {transaction?.payment.updateDate &&
                  formatDate(transaction.payment.updateDate)}
              </p>
            </DescriptionList.Description>
            <DescriptionList.Label className="w-64">
              {t(
                `settlementCreditReceiveDetail.${settlementCreditLineType}.transfer`,
              )}
            </DescriptionList.Label>
            <DescriptionList.Description className="flex flex-col justify-center">
              <SkeletonBox className="h-full w-32" loading={loading}>
                <span>
                  {t('transactionDetail.transactionId', {
                    id: data?.settlementCreditTransfers.list[0]?.id,
                  })}
                </span>
                <TextLink
                  onClick={handleViewSettlementCreditTransferClick}
                  variant="primary"
                >
                  {t('settlementCreditReceiveDetail.view')}
                </TextLink>
              </SkeletonBox>
            </DescriptionList.Description>
          </DescriptionList>
        </div>
      </GraphQLErrorBoundary>
    </Content>
  )
}
